'use client'

import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { Loader2 } from 'lucide-react'
import type { ProviderFormState, AIProvider } from './types'

interface ProviderDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  formData: ProviderFormState
  onChange: (next: ProviderFormState) => void
  onSave: () => Promise<void> | void
  isSaving: boolean
  editingProvider: AIProvider | null
}

export function ProviderDialog({
  open,
  onOpenChange,
  formData,
  onChange,
  onSave,
  isSaving,
  editingProvider,
}: ProviderDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>
            {editingProvider ? 'Edit Provider' : 'Add Provider'}
          </DialogTitle>
          <DialogDescription>
            {editingProvider
              ? 'Update provider configuration and API key'
              : 'Add a new AI provider with API credentials'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="name">
              <span>Provider Name</span> {!editingProvider && <span className="text-red-500">*</span>}
            </Label>
            <Input
              id="name"
              placeholder="openai"
              value={formData.name}
              onChange={(e) => onChange({ ...formData, name: e.target.value })}
              disabled={!!editingProvider}
            />
            <p className="text-xs text-gray-500">
              Unique identifier (lowercase, no spaces)
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="display_name">
              <span>Display Name</span> <span className="text-red-500">*</span>
            </Label>
            <Input
              id="display_name"
              placeholder="OpenAI"
              value={formData.display_name}
              onChange={(e) => onChange({ ...formData, display_name: e.target.value })}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="api_key">
              <span>API Key</span> {!editingProvider && <span className="text-red-500">*</span>}
            </Label>
            <Input
              id="api_key"
              type="password"
              placeholder={editingProvider ? 'Leave empty to keep current key' : 'sk-...'}
              value={formData.api_key}
              onChange={(e) => onChange({ ...formData, api_key: e.target.value })}
            />
            <p className="text-xs text-gray-500">
              API key will be encrypted before storage
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="base_url">Base URL (optional)</Label>
            <Input
              id="base_url"
              placeholder="https://api.openai.com/v1"
              value={formData.base_url}
              onChange={(e) => onChange({ ...formData, base_url: e.target.value })}
            />
            <p className="text-xs text-gray-500">
              API endpoint URL. Leave empty to use the provider&apos;s default (e.g. api.openai.com).
              Set it to route via a proxy, Azure, or a gateway.
            </p>
          </div>

          <div className="flex items-center space-x-2">
            <Switch
              id="is_enabled"
              checked={formData.is_enabled}
              onCheckedChange={(checked) => onChange({ ...formData, is_enabled: checked })}
            />
            <Label htmlFor="is_enabled">Enable provider</Label>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSaving}>
            Cancel
          </Button>
          <Button onClick={onSave} disabled={isSaving}>
            {isSaving ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
              'Save'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
